/**
 * sociamonials.ts — everything about sending approved social posts through Sociamonials that does not need the network:
 * network codes, reading their replies, building the post request, deciding how a post is sent, and the connection settings.
 *
 * Safety rules, kept in code:
 *   - A post is never sent with "publish now". It is scheduled for its date or saved as a draft in Sociamonials.
 *   - Only a post approved here is ever sent.
 */
import { z } from "zod";

/** Sociamonials network codes <-> the networks a CMS channel can be on. */
export const SM_NETWORKS = [
  { code: "ln", service: "linkedin", label: "LinkedIn" },
  { code: "tw", service: "x", label: "X" },
  { code: "fb", service: "facebook", label: "Facebook" },
  { code: "in", service: "instagram", label: "Instagram" },
  { code: "thrd", service: "threads", label: "Threads" },
  { code: "blsk", service: "bluesky", label: "Bluesky" },
  { code: "tiktok", service: "tiktok", label: "TikTok" },
  { code: "yt", service: "youtube", label: "YouTube" },
] as const;
export type SmNetworkCode = (typeof SM_NETWORKS)[number]["code"];

const LONG_NAMES: Record<string, SmNetworkCode> = { linkedin: "ln", twitter: "tw", x: "tw", facebook: "fb", instagram: "in", threads: "thrd", bluesky: "blsk", tiktok: "tiktok", youtube: "yt" };

/** "ln" / "LinkedIn" / "linkedin" -> "ln"; anything we can't send to (Pinterest, Google Business, …) -> null. */
export function smNetworkCode(raw: unknown): SmNetworkCode | null {
  const v = String(raw ?? "").trim().toLowerCase();
  if (SM_NETWORKS.some((n) => n.code === v)) return v as SmNetworkCode;
  return LONG_NAMES[v] ?? null;
}
export const smServiceOf = (code: SmNetworkCode) => SM_NETWORKS.find((n) => n.code === code)!.service;
export const smCodeOfService = (service: string): SmNetworkCode | null => SM_NETWORKS.find((n) => n.service === service)?.code ?? null;

// -------------------------------------------------------------------------------------------------------- profiles

export interface SmProfile {
  ref: string;
  name: string;
  network: SmNetworkCode | null;
  canPublish: boolean;
}

/** Reads the profile list whatever wrapper it comes in. Rows without a profile_ref are dropped. */
export function parseSmProfiles(raw: unknown): SmProfile[] {
  const list: unknown[] = Array.isArray(raw) ? raw : Array.isArray((raw as any)?.social_profiles) ? (raw as any).social_profiles : Array.isArray((raw as any)?.profiles) ? (raw as any).profiles : Array.isArray((raw as any)?.data) ? (raw as any).data : [];
  return list.flatMap((r): SmProfile[] => {
    if (!r || typeof r !== "object") return [];
    const o = r as Record<string, unknown>;
    const ref = o.profile_ref ?? o.ref;
    if (ref === undefined || ref === null || String(ref).trim() === "") return [];
    const name = [o.display_name, o.name, o.account_name, o.profile_name, o.title].find((x) => typeof x === "string" && x.trim() !== "") as string | undefined;
    return [{ ref: String(ref), name: (name ?? String(ref)).trim(), network: smNetworkCode(o.network_code ?? o.network ?? o.service ?? o.platform), canPublish: o.agent_can_publish_to === true }];
  });
}

// -------------------------------------------------------------------------------------------------------- sending

/** Soonest a post may be scheduled for; anything sooner is saved as a draft instead of risking an immediate send. */
export const SM_MIN_LEAD_MS = 5 * 60_000;

export type SmSendMode = "scheduled" | "draft";
export function smModeFor(scheduledFor: string | null, now: Date): SmSendMode {
  if (!scheduledFor) return "draft";
  const t = Date.parse(scheduledFor);
  return Number.isFinite(t) && t - now.getTime() >= SM_MIN_LEAD_MS ? "scheduled" : "draft";
}

export interface SmPostInput {
  id: string;
  text: string;
  scheduledFor: string | null;
  profileRef: string;
  network: SmNetworkCode;
  imageUrls?: readonly string[];
  videoUrl?: string | null;
  youtubeTitle?: string | null;
  /** The exact name of a Post Preset saved in Sociamonials (it can carry TikTok and YouTube settings). */
  postPreset?: string | null;
}

/** The exact request body for POST /api/v1/posts. Never contains mode "publish_now". */
export function buildSmPostRequest(post: SmPostInput, now: Date, attempt = 1) {
  const mode = smModeFor(post.scheduledFor, now);
  const images = (post.imageUrls ?? []).filter(Boolean);
  const title = post.network === "yt" ? post.youtubeTitle?.trim() : "";
  return {
    mode,
    message: post.text.trim(),
    networks: { [post.network]: { profile_refs: [post.profileRef], ...(title ? { title } : {}) } },
    ...(images.length > 0 ? { image_urls: images } : {}),
    ...(post.videoUrl ? { video_url: post.videoUrl } : {}),
    ...(post.postPreset?.trim() ? { post_preset: post.postPreset.trim() } : {}),
    ...(mode === "scheduled" ? { publish_at: new Date(post.scheduledFor!).toISOString() } : {}),
    idempotency_key: `cms-social-${post.id}-${attempt}`,
  };
}

// -------------------------------------------------------------------------------------------------------- status

export const SM_STATUSES = ["draft", "pending_approval", "scheduled", "queued", "delivered", "failed", "canceled", "unknown"] as const;
export type SmStatus = (typeof SM_STATUSES)[number];
export const SM_STATUS_LABEL: Record<SmStatus, string> = {
  draft: "Draft in Sociamonials",
  pending_approval: "Waiting for approval in Sociamonials",
  scheduled: "Scheduled in Sociamonials",
  queued: "In the Sociamonials queue",
  delivered: "Published",
  failed: "Failed to publish",
  canceled: "Cancelled in Sociamonials",
  unknown: "Sent to Sociamonials",
};
export const SM_FINAL: readonly SmStatus[] = ["delivered", "failed", "canceled"];

/** Reads a post (or the create reply) and returns its id and our single status word. */
export function interpretSmPost(raw: unknown): { postId: number | null; status: SmStatus; error: string | null } {
  const root = (raw && typeof raw === "object" ? raw : {}) as Record<string, any>;
  const p = (root.post && typeof root.post === "object" ? root.post : root.data && typeof root.data === "object" ? root.data : root) as Record<string, any>;
  const idRaw = p.post_id ?? p.id ?? root.post_id;
  const postId = Number.isInteger(Number(idRaw)) && Number(idRaw) > 0 ? Number(idRaw) : null;

  const nets = p.networks && typeof p.networks === "object" ? Object.values(p.networks as Record<string, any>) : [];
  const netStates = nets.map((n) => String(n?.delivery_status ?? "").toLowerCase()).filter(Boolean);
  const failedMsg = nets.map((n) => (String(n?.delivery_status ?? "").toLowerCase() === "failed" ? n?.error ?? n?.error_message : null)).find((m) => typeof m === "string" && m);

  const word = String(p.status ?? "").toLowerCase().replace(/\s+/g, "_");
  let status: SmStatus = (SM_STATUSES as readonly string[]).includes(word) ? (word as SmStatus) : word === "cancelled" ? "canceled" : "unknown";
  if (status === "unknown" || status === "scheduled" || status === "queued") {
    if (netStates.length > 0 && netStates.every((s) => s === "delivered")) status = "delivered";
    else if (netStates.includes("failed")) status = "failed";
  }
  if (status === "unknown" && p.is_draft === true) status = "draft";
  return { postId, status, error: status === "failed" ? (typeof failedMsg === "string" ? failedMsg : "Sociamonials could not publish this post.") : null };
}

/** Plain-language message for an error.code from Sociamonials. */
export function smErrorMessage(code: string | null, fallback: string): string {
  switch (code) {
    case "invalid_credentials":
    case "malformed_credential":
    case "missing_credentials":
      return "Sociamonials did not accept the saved key. Check it under API keys.";
    case "api_subscription_inactive":
      return "API access is not active on this Sociamonials account.";
    case "api_access_disabled":
      return "Sociamonials has switched API access off. Try again later.";
    case "api_write_operations_disabled":
      return "Sociamonials has turned off posting through the API for this account.";
    case "profile_not_assigned":
      return "This profile is not assigned to the API key. Assign it in Sociamonials, then sync profiles again.";
    case "permission_denied":
    case "workspace_access_denied":
      return "The API key does not have permission to do that in this workspace.";
    case "attached_user_no_reports_permission":
      return "The person this API key acts as cannot open Reports in Sociamonials. Give them Reports access there.";
    case "attached_user_unavailable":
      return "The person this API key acts as is not available in Sociamonials.";
    case "rate_limited":
      return "Sociamonials is limiting requests. It will be tried again shortly.";
    case "validation_failed":
      return fallback ? `Sociamonials refused the post: ${fallback}` : "Sociamonials refused the post.";
    default:
      return fallback || "Sociamonials returned an error.";
  }
}

// -------------------------------------------------------------------------------------------------------- settings

export const smConnectionSchema = z.object({
  workspaceId: z.number().int().positive().nullable(),
  workspaceName: z.string().nullable(),
  /** Master switch: nothing is sent while off. */
  sendEnabled: z.boolean(),
  /** Send approved posts without a button click (still only approved posts). */
  autoSend: z.boolean(),
  /** Name of a saved Post Preset in Sociamonials, sent with every post. Empty = none. */
  postPreset: z.string().nullable().default(null),
  alertsConnected: z.boolean(),
  lastSyncAt: z.string().nullable(),
  keySaved: z.boolean(),
});
export type SmConnection = z.infer<typeof smConnectionSchema>;
export const smConnectionSaveSchema = z.object({
  siteId: z.string().min(1).max(100),
  workspaceId: z.number().int().positive().nullable(),
  workspaceName: z.string().trim().max(200).nullable().optional(),
  sendEnabled: z.boolean(),
  autoSend: z.boolean(),
  postPreset: z.string().trim().max(120).nullable().optional(),
});
export const smWorkspacesSchema = z.object({ items: z.array(z.object({ id: z.number().int(), name: z.string() })) });
export const smTestResultSchema = z.object({ ok: z.boolean(), message: z.string(), workspaces: z.array(z.object({ id: z.number().int(), name: z.string() })).default([]) });
export const smSyncResultSchema = z.object({ added: z.number().int(), updated: z.number().int(), notAllowed: z.number().int(), unsupported: z.number().int(), message: z.string() });
export const smSendResultSchema = z.object({ postId: z.string(), smPostId: z.number().int().nullable(), smStatus: z.enum(SM_STATUSES), mode: z.enum(["scheduled", "draft"]), message: z.string() });
export type SmSendResult = z.infer<typeof smSendResultSchema>;

export const smAlertSchema = z.object({ channelId: z.string(), name: z.string(), kind: z.enum(["reconnect", "expiring"]), message: z.string() });
export const smStatusResponseSchema = z.object({ connection: smConnectionSchema, alerts: z.array(smAlertSchema) });
export type SmStatusResponse = z.infer<typeof smStatusResponseSchema>;
